import { CommonModule } from '@angular/common';
import { Component, DestroyRef, effect, input, OnInit, signal } from '@angular/core';
import { Inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivationEnd, RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { distinctUntilChanged, filter, map } from 'rxjs';

import { BreakpointService } from '@shure/cloud/shared/services/media-breakpoints';
import { SecondaryNavService } from '@shure/cloud/shared/services/secondary-nav-service';
import { PipesModule } from '@shure/cloud/shared/ui/pipes';
import { APP_ENVIRONMENT, AppEnvironment } from '@shure/cloud/shared/utils/config';
import { CloudFeatureFlagsService, FEATURE_FLAG_SERVICE } from '@shure/cloud/shared/utils/feature-flags';
import { CloudNavigationService } from '@shure/cloud/shared/utils/navigation';
import {
	PrismIconComponent,
	PrismNavButtonComponent,
	PrismNavGroupComponent,
	PrismNavToggleButtonComponent
} from '@shure/prism-angular-components';

import { ApplicationType, NavigationItems, PermissionsList } from '../models/side-nav.model';
import { SideNavItemProvider } from '../services/side-nav-item.provider';

@Component({
	selector: 'sh-side-nav',
	imports: [
		CommonModule,
		RouterModule,
		TranslocoModule,
		PipesModule,
		PrismIconComponent,
		PrismNavGroupComponent,
		PrismNavButtonComponent,
		PrismNavToggleButtonComponent,
		PrismNavToggleButtonComponent
	],
	templateUrl: './side-nav.component.html',
	styleUrl: './side-nav.component.scss'
})
export class SideNavComponent implements OnInit {
	public readonly hasSecondaryNav = input.required<boolean>();
	public readonly permissionsList = input<PermissionsList | undefined>();
	public readonly orgId = input<string | undefined>();

	public readonly collapsePrismSecondaryNav = this.breakpointService.collapsePrismSecondaryNav;
	public showSecondaryNav = this.secondaryNavService.showSecondaryNav;
	public navItems = signal<NavigationItems[]>(this.sideNavItemProvider.getItems());

	constructor(
		private router: Router,
		private secondaryNavService: SecondaryNavService,
		@Inject(APP_ENVIRONMENT) public appEnv: AppEnvironment,
		private destroyRef: DestroyRef,
		private cloudNavigationService: CloudNavigationService,
		public breakpointService: BreakpointService,
		@Inject(FEATURE_FLAG_SERVICE) private featureFlagsService: CloudFeatureFlagsService,
		private readonly sideNavItemProvider: SideNavItemProvider
	) {
		effect(() => {
			const collapsePrismSecondaryNav = this.collapsePrismSecondaryNav();
			if (collapsePrismSecondaryNav) {
				this.toggleShowSecondaryNav();
			}
		});
	}

	public ngOnInit(): void {
		this.featureFlagsService
			.getFeatureFlag$('shure-cloud-rbac-custom-roles-enabled')
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((flag: boolean) => {
				this.filterNavItemsByPermission(flag);
			});
		this.updateNavItemUrl();

		// Set initial active nav item on page load
		this.updateActiveNavItem();
		this.router.events
			.pipe(
				filter((event): event is ActivationEnd => event instanceof ActivationEnd),
				map(() => this.router.url), // get the full URL after activation
				distinctUntilChanged(), // only trigger if URL actually changed
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => {
				this.updateActiveNavItem();
			});
	}

	/**
	 * Handle toggling of secondary nav button
	 */
	public toggleShowSecondaryNav(): void {
		this.secondaryNavService.showSecondaryNav.update((current) => !current);
	}

	/**
	 * Handles click events on nav buttons.
	 * - Prevents default navigation if the navItem belongs to the current application.
	 *   (So router navigation takes over instead of a full page reload.)
	 */
	public handleNavButtonClick(event: MouseEvent, navItem: NavigationItems): void {
		if (navItem.applicationType === this.appEnv.application) {
			event.preventDefault();
		}
	}

	/**
	 * Removes navigation items that the user does not have permission to access.
	 * - Keeps items that do not require any permission.
	 * - Keeps items for which the user has the required permission.
	 * - Removes items for which the user lacks the required permission.
	 */
	private filterNavItemsByPermission(customRolesEnabled: boolean): void {
		// Keep item if no permission required OR user has the permission
		const updated = this.navItems().filter((item) => {
			if (item.route === 'roles') return customRolesEnabled;
			if (!item.permission) return true;
			return this.permissionsList()?.[<keyof PermissionsList>item.permission] !== false;
		});
		this.navItems.set(updated);
	}

	/**
	 * Update the navigation items with their respective navigation URLs.
	 */
	private updateNavItemUrl(): void {
		const updated = this.navItems().map((item) => ({
			...item,
			navigateUrl: this.getNavToPath(item)
		}));

		this.navItems.set(updated);
	}

	/**
	 * Returns the navigation path (internal or external) for the given navigation item.
	 * - If the navItem belongs to a different application, build a full URL using CloudNavigationService.
	 * - If it belongs to the current application, return a relative route (e.g., `/my-route`).
	 */
	private getNavToPath(navItem: NavigationItems): string {
		if (navItem.applicationType !== this.appEnv.application) {
			let navigateUrl = '';
			if (navItem.applicationType === ApplicationType.CloudDeviceManagement) {
				const orgId = this.orgId() ? `?orgId=${this.orgId()}` : '';
				navigateUrl = this.cloudNavigationService.getNavigationUrl('/devices', 'deviceManagement', orgId);
			} else {
				navigateUrl = this.cloudNavigationService.getShureCloudUrl(`/${navItem.route}`);
			}
			return navigateUrl;
		}
		return `/${navItem.route}`;
	}

	private updateActiveNavItem(): void {
		this.navItems.update((items) =>
			items.map((item) => ({
				...item,
				selected: item.checkActive
					? item.checkActive(this.router)
					: this.router.isActive(`/${item.route}`, {
							paths: 'subset',
							queryParams: 'ignored',
							fragment: 'ignored',
							matrixParams: 'ignored'
					  })
			}))
		);
	}
}
